import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { FilterBar, type FilterField } from '@/components/ui/filter-bar';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Skeleton } from '@/components/ui/skeleton';
import { EVENT_TYPE_FILTERS } from '@/features/events/event-enums';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toApiDate as apiDate } from '@/lib/store-time';
import {
  useHeatmap,
  type HeatmapKind,
  type HeatmapModel,
  type HeatmapDay,
  type HeatmapHour,
} from './heatmap.api';

/**
 * Sales and bookings by day and hour.
 *
 * Drawn as a grid of cells rather than through a charting library: a heatmap is
 * seven rows of coloured boxes, and the dependency bought nothing except a
 * tooltip. It also gets two things right that the chart did not — the cell
 * values were passed through a thousands formatter, so any hour past 999
 * reached the chart as the string "1,234"; and the hour's own event list, which
 * the API has always returned, had nowhere to go.
 *
 * Seven day-rows by twenty-four hour-columns does not fit a phone: at 360px a
 * cell is about 13px and the hour labels collide into an unreadable band. The
 * grid keeps a legible minimum width and scrolls sideways instead — a swipe is
 * a smaller cost than a chart nobody can read.
 */

const ANY = 'ANY';

/** Sunday to Saturday of the current week, which is what the original opened on. */
function thisWeek() {
  const now = new Date();
  const start = new Date(now);
  start.setDate(now.getDate() - now.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}

/**
 * How hot one cell is, 0–1, against the range the API reported.
 *
 * A count of zero stays at zero rather than picking up the floor of the scale:
 * "nothing happened" and "the quietest hour that had something" are different
 * facts, and on a heatmap the difference has to be visible.
 */
function intensity(count: number, min: number, max: number) {
  if (count <= 0) return 0;
  if (max <= min) return 1;
  return 0.15 + (0.85 * (count - min)) / (max - min);
}

export function HeatmapPage({ kind }: { kind: HeatmapKind }) {
  const { t } = useTranslation();

  const week = useMemo(thisWeek, []);
  const [start, setStart] = useState(week.start);
  const [end, setEnd] = useState(week.end);
  const [eventType, setEventType] = useState(ANY);
  const [selected, setSelected] = useState<{
    day: HeatmapDay;
    hour: HeatmapHour;
  } | null>(null);

  const model: HeatmapModel = useMemo(
    () => ({
      eventType: eventType === ANY ? null : Number(eventType),
      startDate: apiDate(start, false),
      endDate: apiDate(end, true),
    }),
    [eventType, start, end]
  );

  const heatmap = useHeatmap(kind, model);
  const days = heatmap.data?.days ?? [];
  const min = heatmap.data?.minValue ?? 0;
  const max = heatmap.data?.maxValue ?? 0;

  // Every day carries the same hour columns, so the header comes off the first.
  const hourLabels = (days[0]?.hours ?? []).map((hour) => hour.name ?? '');

  const filterFields: FilterField[] = [
    {
      id: 'start',
      label: t('heatmaps.from'),
      value: start,
      onChange: setStart,
      type: 'date',
      resetValue: '',
    },
    {
      id: 'end',
      label: t('heatmaps.to'),
      value: end,
      onChange: setEnd,
      type: 'date',
      resetValue: '',
    },
    {
      id: 'eventType',
      label: t('heatmaps.eventType'),
      value: eventType,
      onChange: setEventType,
      sort: false,
      options: [
        { value: ANY, label: t('heatmaps.any') },
        ...EVENT_TYPE_FILTERS.map((type) => ({
          value: String(type.value),
          label: t(type.labelKey),
        })),
      ],
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          kind === 'sales' ? t('heatmaps.salesTitle') : t('heatmaps.bookingsTitle')
        }
        description={
          kind === 'sales'
            ? t('heatmaps.salesSubtitle')
            : t('heatmaps.bookingsSubtitle')
        }
        actions={
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('heatmaps.reload')}
            disabled={heatmap.isFetching}
            onClick={() => void heatmap.refetch()}
          >
            <RefreshCw className={heatmap.isFetching ? 'animate-spin' : ''} />
          </Button>
        }
      />

      <FilterBar
        busy={heatmap.isFetching}
        fields={filterFields}
        summary={
          // States the range in words. It matters more once the date picker is
          // behind a sheet: a heatmap with no visible range is just a grid of
          // colours.
          <p className="text-muted-foreground text-sm">
            {formatDate(start)} – {formatDate(end)}
          </p>
        }
      />

      {heatmap.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('heatmaps.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void heatmap.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {heatmap.isPending && <Skeleton className="h-72 rounded-xl" />}

      {heatmap.isSuccess && days.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={heatmap.isFetching} />
          <p
            className={cn(
              'text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm',
              heatmap.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {t('heatmaps.noData')}
          </p>
        </div>
      )}

      {days.length > 0 && (
        <div className="space-y-3">
          {/* The spinner sits outside the horizontal scroller, which would
              otherwise pin it instead of letting it stick to the page. */}
          <div className="relative rounded-xl">
            <BusyOverlay show={heatmap.isFetching} />
            <div className="bg-card overflow-x-auto rounded-xl border p-3">
              <div
                className={cn(
                  'min-w-[46rem]',
                  heatmap.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <table className="w-full border-separate border-spacing-0.5">
                  <thead>
                    <tr>
                      <th className="w-24" />
                      {hourLabels.map((label) => (
                        <th
                          key={label}
                          className="text-muted-foreground pb-1 text-[10px] font-normal"
                        >
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {days.map((day) => (
                      <tr key={day.dateString ?? day.date ?? ''}>
                        <th className="text-muted-foreground w-24 pr-2 text-right text-xs font-normal whitespace-nowrap">
                          {day.dateString}
                        </th>
                        {(day.hours ?? []).map((hour) => {
                          const count = hour.count ?? 0;
                          const heat = intensity(count, min, max);
                          const isSelected =
                            selected?.day === day && selected?.hour === hour;
                          return (
                            <td key={hour.name ?? ''} className="p-0">
                              <button
                                type="button"
                                onClick={() => setSelected({ day, hour })}
                                title={`${day.dateString} ${hour.name} · ${formatNumber(count)}`}
                                className={cn(
                                  'focus-visible:ring-ring block h-8 w-full rounded-sm outline-none focus-visible:ring-2',
                                  count === 0 && 'bg-muted',
                                  isSelected && 'ring-foreground ring-2'
                                )}
                                style={
                                  count > 0
                                    ? {
                                        backgroundColor: 'var(--chart-1)',
                                        opacity: heat,
                                      }
                                    : undefined
                                }
                              >
                                <span className="sr-only">
                                  {day.dateString} {hour.name}:{' '}
                                  {formatNumber(count)}
                                </span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="text-muted-foreground mt-3 flex items-center gap-2 text-xs">
                  <span>{formatNumber(min)}</span>
                  <span className="flex h-2 flex-1 overflow-hidden rounded-full">
                    {Array.from({ length: 12 }).map((_, index) => (
                      <span
                        key={index}
                        className="flex-1"
                        style={{
                          backgroundColor: 'var(--chart-1)',
                          opacity: 0.15 + (0.85 * index) / 11,
                        }}
                      />
                    ))}
                  </span>
                  <span>{formatNumber(max)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* The cell's own detail. A tooltip could carry the count, but not the
              list of what was on — and on a touch screen there is no hover to
              open one with. */}
          <div className="bg-card rounded-xl border p-3">
            {selected ? (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="text-sm font-medium">
                    {selected.day.dateString} · {selected.hour.name}
                  </p>
                  <p className="text-xl font-semibold tabular-nums">
                    {formatNumber(selected.hour.count)}
                  </p>
                </div>
                {(selected.hour.events ?? []).length > 0 ? (
                  <ul className="text-muted-foreground space-y-0.5 text-xs">
                    {selected.hour.events!.map((event, index) => (
                      <li key={`${event.eventName ?? ''}-${index}`}>
                        {event.eventName}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground text-xs">
                    {t('heatmaps.nothingInHour')}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                {t('heatmaps.selectACell')}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function SalesHeatmapPage() {
  return <HeatmapPage kind="sales" />;
}

export function BookingsHeatmapPage() {
  return <HeatmapPage kind="bookings" />;
}
